import { createAsyncThunk } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import { RootState } from "../../root-redux/RootState";
import { Logger } from "../../utils/Logger";
import { postRequest } from "../utils/PostRequest";

type AgentRequest = { message: string };

export interface TroubleshootWorkflowRequest {
    workflowId: string;
    subscriptionKeys: string[];
    message?: string;
}

interface SupportAgentResponse {
    threadId: string;
    response: string;
}

export const postNebulaSupportAgentMessage = createAsyncThunk<
    { threadId: string; response: string },
    AgentRequest,
    { state: RootState; rejectValue: { correlationId: string; statusCode: number; message: string } }
>(
    "nebulaSupportAgent/postMessage",
    async (request: AgentRequest, { getState, rejectWithValue }) => {
        const threadId = getState().nebulaSupportAgent.threadId;
        const correlationId = uuidv4();
        const environment = getState().env.selectedEnvItem || "uat";

        try {
            Logger.Instance.trackTrace("start NebulaSupportAgent postMessage", {
                correlationId,
                threadId,
            });

            const response = await postRequest<SupportAgentResponse, { ThreadId: string; UserPrompt: string }>(
                `${environment}/supportagent/chat`,
                { ThreadId: threadId ?? "", UserPrompt: request.message },
                undefined,
                undefined,
                correlationId
            );

            Logger.Instance.trackTrace("completed NebulaSupportAgent postMessage", {
                correlationId,
                threadId: response.data.threadId,
            });

            return { threadId: response.data.threadId, response: response.data.response };
        } catch (error: any) {
            Logger.Instance.trackException({ error: error as Error }, {
                event: "error NebulaSupportAgent postMessage",
                correlationId,
            });
            const statusCode = error?.response?.status || (error?.code === "ECONNABORTED" ? 408 : 0);
            const msg = error?.response?.data || error?.message || "Unknown error";
            return rejectWithValue({ correlationId, statusCode, message: typeof msg === 'string' ? msg : JSON.stringify(msg) });
        }
    }
);

export const troubleshootWorkflow = createAsyncThunk<
    { threadId: string; response: string },
    TroubleshootWorkflowRequest,
    { state: RootState; rejectValue: { correlationId: string; statusCode: number; message: string } }
>(
    "nebulaSupportAgent/troubleshootWorkflow",
    async (request: TroubleshootWorkflowRequest, { getState, rejectWithValue }) => {
        const threadId = getState().nebulaSupportAgent.threadId;
        const correlationId = uuidv4();
        const environment = getState().env.selectedEnvItem || "uat";

        try {
            Logger.Instance.trackTrace("start NebulaSupportAgent troubleshootWorkflow", {
                correlationId,
                threadId,
                workflowId: request.workflowId,
            });

            const response = await postRequest<
                SupportAgentResponse,
                { ThreadId: string; UserPrompt: string; SubscriptionKeys: string[] }
            >(
                `${environment}/supportagent/workflow/${request.workflowId}/troubleshoot`,
                {
                    ThreadId: threadId ?? "",
                    UserPrompt: request.message || "Troubleshoot this workflow and resolve any issues.",
                    SubscriptionKeys: request.subscriptionKeys,
                },
                undefined,
                undefined,
                correlationId
            );

            Logger.Instance.trackTrace("completed NebulaSupportAgent troubleshootWorkflow", {
                correlationId,
                threadId: response.data.threadId,
                workflowId: request.workflowId,
            });

            return { threadId: response.data.threadId, response: response.data.response };
        } catch (error: any) {
            Logger.Instance.trackException({ error: error as Error }, {
                event: "error NebulaSupportAgent troubleshootWorkflow",
                correlationId,
                workflowId: request.workflowId,
            });
            const statusCode = error?.response?.status || (error?.code === "ECONNABORTED" ? 408 : 0);
            const msg = error?.response?.data || error?.message || "Unknown error";
            return rejectWithValue({ correlationId, statusCode, message: typeof msg === 'string' ? msg : JSON.stringify(msg) });
        }
    }
);
